import { useQueryClient } from '@tanstack/react-query'
import { api } from '../http'
import { buildJsonApiParams } from '../jsonApi'
import { queryKeys } from '../queryKeys'
import {
    useApiQuery,
    useApiInfiniteQuery,
    useApiPagedInfiniteQuery,
    useApiMutation,
} from './_base'

/**
 * GET /postGroups — paginated list, scoped to a company via filter[company_id].
 *
 * @param {import('../types').ListParams & { companyId: number|string }} params
 */
export function usePostGroupList(params = {}, options = {}) {
    const { companyId, ...rest } = params
    const apiParams = buildJsonApiParams({
        ...rest,
        filter: { ...(rest.filter || {}), company_id: companyId },
    })
    return useApiQuery({
        queryKey: queryKeys.postGroups.list({ companyId, ...apiParams }),
        url: '/postGroups',
        params: apiParams,
        options: { enabled: !!companyId, ...options },
    })
}

/** GET /postGroups — infinite (link-based pagination, JSON:API links.next) */
export function usePostGroupsInfinite(params = {}, options = {}) {
    const { companyId, ...rest } = params
    const apiParams = buildJsonApiParams({
        ...rest,
        filter: { ...(rest.filter || {}), company_id: companyId },
    })
    return useApiInfiniteQuery({
        queryKey: queryKeys.postGroups.infinite({ companyId, ...apiParams }),
        url: '/postGroups',
        params: apiParams,
        options: { enabled: !!companyId, ...options },
    })
}

/** GET /postGroups — page-number infinite variant (use when you want N pages of a size). */
export function usePostGroupsPagedInfinite(params = {}, options = {}) {
    const { companyId, ...rest } = params
    return useApiPagedInfiniteQuery({
        queryKey: queryKeys.postGroups.infinite({
            companyId,
            ...buildJsonApiParams(rest),
        }),
        url: '/postGroups',
        buildPageParams: (page) =>
            buildJsonApiParams({
                ...rest,
                filter: { ...(rest.filter || {}), company_id: companyId },
                page: { ...(rest.page || {}), number: page },
            }),
        options: { enabled: !!companyId, ...options },
    })
}

/** GET /postGroups/{id} */
export function usePostGroup(id, params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    return useApiQuery({
        queryKey: queryKeys.postGroups.detail(id, apiParams),
        url: `/postGroups/${id}`,
        params: apiParams,
        options: { enabled: !!id, ...options },
    })
}

/** POST /companies/{companyId}/postGroups */
export function useCreatePostGroup() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['postGroups', 'create'],
        mutationFn: ({ companyId, ...payload }) =>
            api.post(`/companies/${companyId}/postGroups`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
            qc.invalidateQueries({ queryKey: queryKeys.posts.all })
        },
    })
}

/** POST /companies/{companyId}/postGroups/bulk */
export function useBulkCreatePostGroups() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['postGroups', 'bulkCreate'],
        mutationFn: ({ companyId, ...payload }) =>
            api.post(`/companies/${companyId}/postGroups/bulk`, {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
            qc.invalidateQueries({ queryKey: queryKeys.posts.all })
        },
    })
}

/** PUT /postGroups/{id} (with optimistic update on cached lists). */
export function useUpdatePostGroup() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['postGroups', 'update'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/postGroups/${id}`, {}, payload),
        onMutate: async ({ id, ...payload }) => {
            await qc.cancelQueries({
                queryKey: queryKeys.postGroups.detail(id),
            })
            const previousDetail = qc.getQueryData(
                queryKeys.postGroups.detail(id, {})
            )
            qc.setQueriesData(
                { queryKey: queryKeys.postGroups.details() },
                (old) => {
                    if (!old?.data) return old
                    if (String(old.data.id) !== String(id)) return old
                    return { ...old, data: { ...old.data, ...payload } }
                }
            )
            return { previousDetail }
        },
        onError: (_err, { id }, ctx) => {
            if (ctx?.previousDetail) {
                qc.setQueryData(
                    queryKeys.postGroups.detail(id, {}),
                    ctx.previousDetail
                )
            }
        },
        onSettled: (_d, _e, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
            qc.invalidateQueries({
                queryKey: queryKeys.postGroups.detail(id),
            })
        },
    })
}

/** DELETE /postGroups/{id} */
export function useDeletePostGroup() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['postGroups', 'delete'],
        mutationFn: (id) => api.delete(`/postGroups/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
            qc.invalidateQueries({ queryKey: queryKeys.posts.all })
        },
    })
}

/** DELETE /postGroups/bulk */
export function useBulkDeletePostGroups() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['postGroups', 'bulkDelete'],
        mutationFn: (ids) =>
            api.delete('/postGroups/bulk', undefined, { ids }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
            qc.invalidateQueries({ queryKey: queryKeys.posts.all })
        },
    })
}

/** POST /postGroups/{id}/publish */
export function usePublishPostGroup() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['postGroups', 'publish'],
        mutationFn: ({ id, ...payload }) =>
            api.post(`/postGroups/${id}/publish`, {}, payload),
        onSuccess: (_d, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.postGroups.all })
            qc.invalidateQueries({
                queryKey: queryKeys.postGroups.detail(id),
            })
            qc.invalidateQueries({ queryKey: queryKeys.posts.all })
        },
    })
}
